/**
 * Clipflow 랜딩 — v3.6 Glass + Neon (Dark)
 * 핸드오프: docs/handoff/video-editor/project/v3_6/v3-6-home.jsx
 *
 * 섹션:
 *  1) Cinematic Hero — 그라데이션 backdrop + 6 floating thumbnails + display headline
 *  2) Feature Portals — 4 진입점 카드 (가로 스크롤)
 *  3) Recent Projects — 최근 작업 그리드
 *  4) Templates — 템플릿 그리드
 *  5) CommandDock — 하단 sticky 명령바
 */
'use client'

import Link from 'next/link'
import V36TopNav from '@/components/layout/V36TopNav'
import { V3Icon, type V3IconName } from '@/components/icons/V3Icon'
import CommandDock from '@/components/home/CommandDock'

type Tone = 'violet' | 'cyan' | 'lime' | 'coral' | 'rose' | 'amber' | 'ink'

export default function HomePage() {
  return (
    <>
      <V36TopNav />
      <main className="v36-fade-in" style={{ paddingBottom: 160 }}>
        <CinematicHero />
        <FeaturePortalRow />
        <RecentSection />
        <TemplatesSection />
      </main>
      <CommandDock />
    </>
  )
}

/* ════════════════════════════════════════════════════════════ */
/* 1. Cinematic Hero                                            */
/* ════════════════════════════════════════════════════════════ */
function CinematicHero() {
  return (
    <section
      style={{
        position: 'relative',
        height: 'min(78vh, 720px)',
        minHeight: 540,
        overflow: 'hidden',
      }}
    >
      {/* 그라데이션 backdrop */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 0,
          background:
            'radial-gradient(ellipse 120% 80% at 70% 30%, rgba(124,92,255,0.45), transparent 60%), radial-gradient(ellipse 100% 80% at 20% 80%, rgba(0,212,224,0.25), transparent 65%)',
        }}
      />

      {/* Floating 썸네일 6장 */}
      <div style={{ position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none' }}>
        <FloatingThumb
          tone="violet"
          style={{
            top: '12%',
            left: '6%',
            width: 180,
            height: 280,
            transform: 'rotate(-6deg)',
            animation: 'v36-float-a 8s ease-in-out infinite',
          }}
        />
        <FloatingThumb
          tone="cyan"
          style={{
            top: '8%',
            right: '8%',
            width: 200,
            height: 130,
            transform: 'rotate(4deg)',
            animation: 'v36-float-b 9s ease-in-out infinite',
          }}
        />
        <FloatingThumb
          tone="lime"
          style={{
            bottom: '18%',
            left: '10%',
            width: 160,
            height: 110,
            transform: 'rotate(8deg)',
            animation: 'v36-float-c 7s ease-in-out infinite',
          }}
        />
        <FloatingThumb
          tone="coral"
          style={{
            bottom: '12%',
            right: '12%',
            width: 220,
            height: 150,
            transform: 'rotate(-4deg)',
            animation: 'v36-float-d 10s ease-in-out infinite',
          }}
        />
        <FloatingThumb
          tone="rose"
          className="v36-pc-only"
          style={{
            top: '44%',
            left: '2%',
            width: 130,
            height: 90,
            transform: 'rotate(-12deg)',
            animation: 'v36-float-b 11s ease-in-out infinite',
          }}
        />
        <FloatingThumb
          tone="amber"
          className="v36-pc-only"
          style={{
            top: '38%',
            right: '2%',
            width: 110,
            height: 160,
            transform: 'rotate(10deg)',
            animation: 'v36-float-c 8s ease-in-out infinite',
          }}
        />
      </div>

      {/* 헤드라인 */}
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          padding: '0 24px',
        }}
      >
        <div
          className="v36-label"
          style={{
            marginBottom: 24,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '6px 14px',
            borderRadius: 999,
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.12)',
          }}
        >
          <span className="v36-live-dot" />
          <span>V1.0 · DARK MODE BETA</span>
        </div>

        <h1
          className="v36-display"
          style={{
            fontSize: 'clamp(44px, 8vw, 104px)',
            margin: 0,
            marginBottom: 18,
            letterSpacing: '-0.05em',
          }}
        >
          상상한 그대로,
          <br />
          <span
            style={{
              background: 'linear-gradient(90deg, #7C5CFF, #00D4E0, #C8FF00)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            영상이 됩니다
          </span>
        </h1>
        <p
          style={{
            fontSize: 'clamp(15px, 1.5vw, 18px)',
            color: 'var(--v36-ink-3)',
            maxWidth: 520,
            margin: 0,
            lineHeight: 1.55,
          }}
        >
          아래 명령창에 한 줄만 입력하세요. AI가 씬을 짜고,
          <br className="v36-pc-only" />
          트랜지션을 정하고, 자막까지 답니다.
        </p>

        {/* 스크롤 힌트 */}
        <div
          aria-hidden
          style={{
            position: 'absolute',
            bottom: 40,
            color: 'var(--v36-ink-4)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 6,
            animation: 'v36-bounce 2s ease-in-out infinite',
          }}
        >
          <span
            className="v36-mono"
            style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.18em' }}
          >
            SCROLL
          </span>
          <V3Icon name="chevD" size={14} />
        </div>
      </div>
    </section>
  )
}

function FloatingThumb({
  tone,
  style,
  className = '',
}: {
  tone: Tone
  style?: React.CSSProperties
  className?: string
}) {
  return (
    <div
      className={`v36-thumb v36-thumb--${tone} ${className}`}
      style={{
        position: 'absolute',
        borderRadius: 12,
        boxShadow:
          '0 24px 64px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.06) inset',
        opacity: 0.85,
        ...style,
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 36,
          height: 36,
          borderRadius: '50%',
          background: 'rgba(0,0,0,0.45)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid rgba(255,255,255,0.4)',
        }}
      >
        <V3Icon name="play" size={12} stroke="#fff" fill="#fff" style={{ marginLeft: 2 }} />
      </div>
    </div>
  )
}

/* ════════════════════════════════════════════════════════════ */
/* 2. Feature Portal Row                                        */
/* ════════════════════════════════════════════════════════════ */
function FeaturePortalRow() {
  return (
    <section style={{ padding: '64px 24px 24px', maxWidth: 1400, margin: '0 auto' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          marginBottom: 24,
          gap: 16,
        }}
      >
        <div>
          <div className="v36-label" style={{ marginBottom: 8 }}>
            네 가지 진입점
          </div>
          <h2 className="v36-h2" style={{ margin: 0 }}>
            어디서 시작할까요?
          </h2>
        </div>
      </div>

      <div
        className="v36-scroll"
        style={{
          display: 'grid',
          gridAutoFlow: 'column',
          gridAutoColumns: 'minmax(280px, 1fr)',
          gap: 14,
          overflowX: 'auto',
          padding: '8px 0 16px',
          scrollSnapType: 'x mandatory',
        }}
      >
        <FeaturePortal
          tone="violet"
          kicker="01 · AI 생성"
          title="한 줄로 만들기"
          desc="아이디어가 영상이 됩니다"
          icon="sparkle"
          primary
          href="/ai"
        />
        <FeaturePortal
          tone="cyan"
          kicker="02 · 씬 에디터"
          title="처음부터 직접"
          desc="타임라인 · 트랜지션"
          icon="film"
          href="/projects"
        />
        <FeaturePortal
          tone="coral"
          kicker="03 · 숏폼"
          title="긴 영상 → 짧게"
          desc="자동 하이라이트"
          icon="scissors"
          href="/shortform"
        />
        <FeaturePortal
          tone="amber"
          kicker="05 · 무협 스튜디오"
          title="로그라인 → 웹소설 영상"
          desc="작품 · 인물 · 회차 자동"
          icon="film"
          href="/wuxia"
        />
        <FeaturePortal
          tone="lime"
          kicker="04 · 업로드"
          title="가져와서 시작"
          desc="MP4 · MOV · 폴더"
          icon="upload"
          href="/projects"
        />
      </div>
    </section>
  )
}

function FeaturePortal({
  tone,
  kicker,
  title,
  desc,
  icon,
  primary,
  href,
}: {
  tone: Tone
  kicker: string
  title: string
  desc: string
  icon: V3IconName
  primary?: boolean
  href: string
}) {
  return (
    <Link
      href={href}
      className="v36-card v36-card--hover"
      style={{
        textAlign: 'left',
        padding: 0,
        scrollSnapAlign: 'start',
        border: primary
          ? '1px solid rgba(124,92,255,0.5)'
          : '1px solid var(--v36-glass-border)',
        boxShadow: primary ? '0 0 32px rgba(124,92,255,0.25)' : undefined,
        overflow: 'hidden',
        cursor: 'pointer',
        display: 'block',
      }}
    >
      <div
        className={`v36-thumb v36-thumb--${tone}`}
        style={{ aspectRatio: '16 / 10', borderRadius: 0 }}
      >
        <div
          style={{
            position: 'absolute',
            top: 14,
            left: 14,
            width: 44,
            height: 44,
            borderRadius: 12,
            background: 'rgba(0,0,0,0.4)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid rgba(255,255,255,0.2)',
          }}
        >
          <V3Icon name={icon} size={20} stroke="#fff" />
        </div>
        {primary && (
          <div style={{ position: 'absolute', top: 14, right: 14 }}>
            <span
              className="v36-chip v36-chip--neon v36-chip--mono"
              style={{ fontSize: 9, height: 22 }}
            >
              RECOMMENDED
            </span>
          </div>
        )}
      </div>
      <div style={{ padding: 18 }}>
        <div className="v36-label" style={{ fontSize: 10, marginBottom: 6 }}>
          {kicker}
        </div>
        <div
          style={{
            fontSize: 18,
            fontWeight: 600,
            letterSpacing: '-0.02em',
            marginBottom: 4,
            color: 'var(--v36-ink)',
          }}
        >
          {title}
        </div>
        <div style={{ fontSize: 13, color: 'var(--v36-ink-3)' }}>{desc}</div>
      </div>
    </Link>
  )
}

/* ════════════════════════════════════════════════════════════ */
/* 3. Recent Projects                                           */
/* ════════════════════════════════════════════════════════════ */
const RECENT: Array<{
  title: string
  tone: Tone
  duration: string
  when: string
  badge?: string
}> = [
  { title: '신메뉴 런칭 — 인스타 릴스', tone: 'violet', duration: '0:28', when: '2 HRS AGO', badge: 'AI' },
  { title: '분당 카페 V-LOG ep.04', tone: 'cyan', duration: '1:42', when: 'YESTERDAY', badge: 'DRAFT' },
  { title: '에어팟 4세대 언박싱', tone: 'coral', duration: '0:58', when: '3 DAYS AGO' },
  { title: '수영장 슬로우모션 컷', tone: 'lime', duration: '0:14', when: '5 DAYS AGO' },
  { title: '회사 소개 컷 모음 v2', tone: 'rose', duration: '2:08', when: 'LAST WEEK' },
  { title: '강의 — React Hooks 입문', tone: 'amber', duration: '8:24', when: 'LAST WEEK' },
  { title: '여행 컷 — 제주 4박', tone: 'ink', duration: '3:45', when: 'OCT 22' },
  { title: '운동 루틴 30일 챌린지 ep.12', tone: 'violet', duration: '1:20', when: 'OCT 18' },
]

function RecentSection() {
  return (
    <section style={{ padding: '32px 24px', maxWidth: 1400, margin: '0 auto' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          marginBottom: 24,
          gap: 16,
        }}
      >
        <div>
          <div className="v36-label" style={{ marginBottom: 8 }}>
            최근 작업
          </div>
          <h2 className="v36-h2" style={{ margin: 0 }}>
            이어서 작업하기
          </h2>
        </div>
        <div className="v36-tabs v36-pc-only">
          <button className="v36-tab v36-tab--active">전체</button>
          <button className="v36-tab">초안</button>
          <button className="v36-tab">완성</button>
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
          gap: 14,
        }}
      >
        {RECENT.map((p, i) => (
          <RecentCard key={i} {...p} />
        ))}
      </div>
    </section>
  )
}

function RecentCard({
  title,
  tone,
  duration,
  when,
  badge,
}: {
  title: string
  tone: Tone
  duration?: string
  when: string
  badge?: string
}) {
  return (
    <div className="v36-card v36-card--hover" style={{ padding: 8, cursor: 'pointer' }}>
      <div
        className={`v36-thumb v36-thumb--${tone}`}
        style={{ aspectRatio: '16 / 9', borderRadius: 10 }}
      >
        {duration && (
          <div
            style={{
              position: 'absolute',
              right: 8,
              bottom: 8,
              padding: '3px 7px',
              borderRadius: 999,
              background: 'rgba(0,0,0,0.7)',
              color: '#fff',
              fontSize: 10,
              fontFamily: 'var(--v36-font-mono)',
            }}
          >
            {duration}
          </div>
        )}
        {badge && (
          <div style={{ position: 'absolute', left: 8, top: 8 }}>
            <span
              className="v36-chip v36-chip--mono"
              style={{
                height: 22,
                fontSize: 9,
                background: 'rgba(0,0,0,0.6)',
                color: '#fff',
                borderColor: 'rgba(255,255,255,0.18)',
              }}
            >
              {badge}
            </span>
          </div>
        )}
      </div>
      <div style={{ padding: '10px 4px 4px' }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </div>
        <div
          className="v36-mono"
          style={{
            fontSize: 10,
            color: 'var(--v36-ink-4)',
            marginTop: 4,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
          }}
        >
          {when}
        </div>
      </div>
    </div>
  )
}

/* ════════════════════════════════════════════════════════════ */
/* 4. Templates                                                 */
/* ════════════════════════════════════════════════════════════ */
const TEMPLATES: Array<{
  title: string
  tone: Tone
  ratio: string
  duration: string
  type: string
}> = [
  { title: '신제품 30초 릴스', tone: 'violet', ratio: '9/16', duration: '0:30', type: '릴스' },
  { title: '카페 룩북 페이드인', tone: 'rose', ratio: '9/16', duration: '0:45', type: '릴스' },
  { title: '유튜브 인트로 5초', tone: 'lime', ratio: '16/9', duration: '0:05', type: '인트로' },
  { title: '체험 후기 6컷', tone: 'coral', ratio: '1/1', duration: '0:60', type: '피드' },
  { title: '포트폴리오 슬로우 컷', tone: 'ink', ratio: '16/9', duration: '1:30', type: '릴' },
  { title: '교육 영상 챕터 분리', tone: 'cyan', ratio: '16/9', duration: '5:00', type: '강의' },
  { title: '이벤트 카운트다운', tone: 'amber', ratio: '1/1', duration: '0:15', type: '프로모' },
  { title: 'Before/After 비교', tone: 'violet', ratio: '9/16', duration: '0:20', type: '릴스' },
]

function TemplatesSection() {
  return (
    <section style={{ padding: '32px 24px 64px', maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 24 }}>
        <div>
          <div className="v36-label" style={{ marginBottom: 8 }}>
            템플릿 · {TEMPLATES.length}개
          </div>
          <h2 className="v36-h2" style={{ margin: 0 }}>
            바로 시작 가능한 템플릿
          </h2>
        </div>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: 12,
        }}
      >
        {TEMPLATES.map((t, i) => (
          <TemplateCard key={i} {...t} />
        ))}
      </div>
    </section>
  )
}

function TemplateCard({
  title,
  tone,
  ratio,
  duration,
  type,
}: {
  title: string
  tone: Tone
  ratio: string
  duration: string
  type: string
}) {
  return (
    <button className="v36-card v36-card--hover" style={{ padding: 6, textAlign: 'left' }}>
      <div className={`v36-thumb v36-thumb--${tone}`} style={{ aspectRatio: ratio, borderRadius: 8 }}>
        <div
          style={{
            position: 'absolute',
            right: 6,
            bottom: 6,
            padding: '2px 6px',
            borderRadius: 999,
            background: 'rgba(0,0,0,0.7)',
            color: '#fff',
            fontSize: 9,
            fontFamily: 'var(--v36-font-mono)',
          }}
        >
          {duration}
        </div>
      </div>
      <div style={{ padding: '8px 4px 2px' }}>
        <div style={{ fontSize: 12, fontWeight: 500 }}>{title}</div>
        <div
          className="v36-mono"
          style={{
            fontSize: 9,
            color: 'var(--v36-ink-4)',
            marginTop: 3,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
          }}
        >
          {type} · {ratio}
        </div>
      </div>
    </button>
  )
}
